<template>
  <base-form ref="formRef" :form>
    <product-variant-selection
      v-model="attributeSelection"
      :attribute-selection-labels="attributeSelectionLabels"
      :handle-next-attribute="handleNextAttribute"
      is-digital-product
      :pending
      :product
      :show-badge="showBadge"
      :show-only-first-selector-on-mobile="showOnlyFirstSelectorOnMobile"
      @select-color="handleSelectColor"
      @select-size="handleSelectSize"
    >
      <slot />
    </product-variant-selection>
    <div class="mt-2" :class="{ '<lg:op-0': showOnlyFirstSelectorOnMobile }">
      <vf-form-field
        v-slot="{ attrs }"
        :hint="replaceAll($t.giftCardAmountMustBe, { amount: minAmount })"
        name="amount"
        :rule="attributeSelection.size === 'X' ? [
          validateMin(customAmount.min, replaceAll($t.giftCardMinAmount, { amount: minAmount })),
          validateMax(customAmount.max, replaceAll($t.giftCardMaxAmount, { amount: maxAmount })),
        ] : []"
      >
        <input-price
          v-model="form.amount"
          v-bind="attrs"
          :currency="product?.currency"
          @update:model-value="handleUpdateAmount"
        />
      </vf-form-field>
      <!-- Recipent details -->
      <div class="mb-2 mt-4 flex items-center b-t b-grey-70 pt-4 space-x-2">
        <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
          <span class="text-xs c-white fw-bold">3</span>
        </div>
        <p class="text-sm fw-bold">
          {{ $t.recipientDetails }}:
        </p>
      </div>
      <vf-form-field
        v-slot="{ attrs }"
        name="fullName"
        :rule="[
          validateRequired($t.recipientsName),
          validateName(),
          validateMinLength(2),
          validateMaxLength(60),
        ]"
      >
        <vf-input v-model="form.fullName" v-bind="attrs" required>
          {{ $t.recipientsName }}
        </vf-input>
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        :hint="$t.emailFormatHint"
        name="email"
        :rule="[validateRequired($t.recipientsEmail), validateEmail()]"
      >
        <vf-input v-model="form.email" v-bind="attrs" class="mt-2" required type="email">
          {{ $t.recipientsEmail }}
        </vf-input>
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        :hint="messageMaxLength ? replaceAll($t.maxCharacters, { max: messageMaxLength }) : undefined"
        name="message"
        :rule="messageMaxLength ? [validateMaxLength(messageMaxLength)] : []"
      >
        <div class="pt-4 space-y-2">
          <p class="b-t b-grey-70 pt-4 text-sm fw-bold">
            {{ $t.optionalMessage }}:
          </p>
          <vf-textarea
            v-model="form.message"
            v-bind="attrs"
            :props-textarea="{ maxlength: messageMaxLength }"
            resizable
          >
            <span class="sr-only">{{ $t.optionalMessage }}</span>
          </vf-textarea>
        </div>
      </vf-form-field>
      <vf-form-field
        v-if="!hideCheckbox"
        v-slot="{ attrs }"
        name="policy"
        :rule="validateRequired('', $t.validators.requiredCheckbox)"
      >
        <vf-checkbox
          v-model="form.policy"
          v-bind="attrs"
          class="mb-2 mt-4"
          required
          :size="checkboxSize"
        >
          <form-legal-disclaimer :content="$t.brandAgreement.termsAndConditions" />
        </vf-checkbox>
      </vf-form-field>
      <div v-if="!hideSubmit" class="mt-4 b-t b-grey-70">
        <vf-button
          aria-live="polite"
          class="mt-4 w-full"
          :loading="pending"
          :size="{ sm: 'sm', lg: 'md' }"
          type="submit"
          @click="handleSubmit"
        >
          {{ $t.addToCart }}
        </vf-button>
      </div>
    </div>
  </base-form>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { GiftOption } from '#types/cart'
import type { AttributeSelection, GiftCardFormFields, ProductAttributeType, ProductExtended } from '#types/product'

const { product } = defineProps<{
  attributeSelectionLabels: Record<string, string>
  error: boolean
  handleNextAttribute?: () => void
  hideCheckbox?: boolean
  hideSubmit?: boolean
  pending: boolean
  product: ProductExtended
  showOnlyFirstSelectorOnMobile?: boolean
}>()

const emit = defineEmits<{
  selectCardStyle: [id: string, url: string, optionLabel: string]
  selectCardAmount: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
  submit: [{ giftOption: GiftOption, customAmount?: { amount?: number } }]
}>()

defineSlots<{
  default: () => void
}>()

const attributeSelection = defineModel<AttributeSelection>('attributeSelection', { required: true })

const form = defineModel<GiftCardFormFields>('form', {
  default: {
    size: '',
    amount: '',
    fullName: '',
    email: '',
    message: '',
    policy: false
  }
})

const { checkboxSize, messageMaxLength, showBadge } = useAppConfig().components.form.giftCard
const { enableColorAsAttribute } = useAppConfig().pages.pdp
const { validateEmail, validateMaxLength, validateName, validateRequired } = useLocalizedValidators()

const formRef = ref<InstanceType<typeof BaseFormType>>()

const customAmount = computed(() => {
  const customVariant = product?.variants.find((variant) => variant.minDigitalProductAmount)
  return {
    min: customVariant?.minDigitalProductAmount || 0,
    max: customVariant?.maxDigitalProductAmount || 0
  }
})

const minAmount = useFormattedPrice(customAmount.value.min, product?.currency, { trailingZeroDisplay: 'stripIfInteger' })
const maxAmount = useFormattedPrice(customAmount.value.max, product?.currency, { trailingZeroDisplay: 'stripIfInteger' })

const attributes = computed(() => {
  // Check if there is any attribute option that needs to be marked as unavailable according to its inventory
  return product?.attributes.map((attribute) => {
    return {
      ...attribute,
      options: attribute.options.filter(({ label }) => label !== 'X'),
    }
  }) || []
})

const sizeAttributes = computed(() => (attributes.value?.filter(({ type }) => type !== 'color') || []))

const preselectColor = () => {
  const { options } = attributes.value.find(({ type }) => enableColorAsAttribute && type === 'color') || {}

  if (!options) return

  const availableOptions = options.filter(({ available }) => available)

  if (options?.length === 1)
    attributeSelection.value.color = options[0].value
  else if (availableOptions.length === 1)
    attributeSelection.value.color = availableOptions[0].value
  else
    attributeSelection.value.color = (options?.find(({ defaultColor }) => defaultColor) || options[0])?.value
}

const selectFirstSize = () => {
  const firstSize = sizeAttributes.value[0]?.options[0]
  form.value.size = firstSize?.value
  emit('selectCardAmount', 'size', firstSize?.value, firstSize?.label)
}

const handleSelectColor = (id, url, optionLabel: string) => {
  emit('selectCardStyle', id, url, optionLabel)
}

const handleSelectSize = (type, value, label: string) => {
  form.value.amount = ''
  form.value.size = value
  emit('selectCardAmount', type, value, label)
}

const handleSubmit = () => {
  emit('submit', {
    giftOption: {
      email: form.value.email,
      to: form.value.fullName,
      message: form.value.message
    },
    ...(form.value.amount ? { customAmount: { amount: +form.value.amount } } : {})
  })
}

const handleUpdateAmount = (value) => {
  if (value && form.value.size !== 'X') {
    form.value.size = 'X'
    emit('selectCardAmount', 'size', 'X', '')
  }

  else if (!value && form.value.size === 'X') {
    selectFirstSize()
  }
}

onBeforeUnmount(() => {
  form.value.fullName = ''
  form.value.email = ''
  form.value.message = ''
  form.value.amount = ''
  form.value.size = ''
  form.value.policy = false
})

onMounted(() => {
  preselectColor()
  selectFirstSize()
})

defineExpose({
  validate: (force?: boolean) => formRef.value?.validate(force),
  reset: () => formRef.value?.reset()
})
</script>
